import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button, Alert, CircularProgress } from '@mui/material';
// Import our custom Grid component instead
import Grid from '../components/Grid';
import { Link as RouterLink } from 'react-router-dom';
import { experts } from '../data/experts';
import {
  Container,
  CardMedia,
  CardActions,
  LinearProgress,
  Stack,
  Paper,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Import useTheme
// Import the TradingBotsSection component
import TradingBotsSection from '../components/TradingBotsSection';
// Import the SentimentService
import { SentimentService, SentimentData } from '../lib/supabase';

const HomePage: React.FC = () => {
  const theme = useTheme(); // Get the theme object
  // Add state for sentiment data
  const [sentimentData, setSentimentData] = useState<SentimentData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get a random selection of experts for the homepage
  const getRandomExperts = (count: number) => {
    const shuffled = [...experts].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  const featuredExperts = getRandomExperts(3);

  // Fetch sentiment data from Supabase
  useEffect(() => {
    const fetchSentimentData = async () => {
      try {
        setLoading(true);
        const data = await SentimentService.getLatestSentimentData();
        
        if (data) {
          setSentimentData(data);
        } else {
          // Fall back to default values if no data is available
          setSentimentData({
            sentiment_usd: {
              percent_bullish: 45,
              percent_bearish: 35,
              relevant_tweet_count: 20
            },
            sentiment_xau: {
              percent_bullish: 65,
              percent_bearish: 25,
              relevant_tweet_count: 18
            }
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching sentiment data:', err);
        setError('Failed to load sentiment data');
        setLoading(false);
      }
    };

    fetchSentimentData();
  }, []);

  // Determine overall market sentiment label
  const getMarketSentiment = (bullish: number, bearish: number): { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' } => {
    const diff = bullish - bearish;
    if (diff > 15) return { label: 'Strongly Bullish', color: 'success' };
    if (diff > 5) return { label: 'Mildly Bullish', color: 'success' };
    if (diff < -15) return { label: 'Strongly Bearish', color: 'error' };
    if (diff < -5) return { label: 'Mildly Bearish', color: 'error' };
    return { label: 'Neutral', color: 'info' };
  };

  return (
    <Box>
      {/* Hero Section - Modern split layout with text left, image right */}
      <Box
        sx={{
          mb: { xs: 4, md: 6 },
          pt: { xs: 2, md: 6 },
          pb: { xs: 3, md: 8 }
        }}
      >
        <Grid container spacing={4} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
          {/* Text Content - Left Side on desktop, Bottom on mobile */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              <Typography 
                variant="h5" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  mb: { xs: 1, md: 3 }, // Further reduced bottom margin on mobile
                  color: 'text.primary',
                  fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' }
                }}
              >
                Market Insights on Gold & USD
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: { xs: 2, md: 4 },
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  textAlign: 'justify' // Adding text justification
                }}
              >
                Your resource for sentiment analysis on XAU (Gold) and USD markets.
                Track expert consensus, market trends and our algorithmic trading bots for better-informed trading decisions.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={RouterLink}
                  to="/sentiment"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ color: 'secondary.main', px: 3, py: 1 }}
                >
                  Market Sentiment
                </Button>
                <Button
                  component={RouterLink}
                  to="/experts"
                  variant="outlined"
                  color="secondary"
                  size="large"
                  sx={{ borderColor: 'secondary.main', px: 3, py: 1 }}
                >
                  Analyst Directory
                </Button>
              </Stack>
            </Box>
          </Grid>
          
          {/* Image - Right Side on desktop, Top on mobile */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                p: { xs: 0.5, sm: 1, md: 2 } // Reduced padding on all platforms
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/images/goldbars.webp`}
                alt="Gold bars"
                sx={{
                  width: '100%',
                  maxWidth: { xs: '250px', sm: '300px', md: '350px' },
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '0 auto',
                  transition: 'transform 0.3s ease',
                  '@media (max-width: 600px)': {
                    transform: 'scale(1.1)'
                  },
                  '@media (min-width: 601px) and (max-width: 960px)': {
                    transform: 'scale(1.25)'
                  },
                  '@media (min-width: 961px)': {
                    transform: 'scale(1.25)'
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Market Sentiment Overview */}
      <Box component="section" sx={{ mb: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom color="secondary.main">
            Market Sentiment Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            AI-powered sentiment analysis from financial market experts
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : sentimentData ? (
          <Grid container spacing={3} justifyContent="center">
            {/* Gold Sentiment Card */}
            <Grid item xs={12} sm={6} md={5}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom align="center">
                    Gold (XAU) Sentiment
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption">Bullish</Typography>
                    <Typography variant="caption">Bearish</Typography>
                  </Box>
                  
                  <LinearProgress
                    variant="determinate"
                    value={sentimentData.sentiment_xau.percent_bullish}
                    color="primary"
                    sx={{ height: 10, borderRadius: 5, mb: 1 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">{sentimentData.sentiment_xau.percent_bullish.toFixed(1)}%</Typography>
                    <Typography variant="body2">{sentimentData.sentiment_xau.percent_bearish.toFixed(1)}%</Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'center', my: 2 }}>
                    {(() => {
                      const sentiment = getMarketSentiment(
                        sentimentData.sentiment_xau.percent_bullish,
                        sentimentData.sentiment_xau.percent_bearish
                      );
                      return <Chip label={sentiment.label} color={sentiment.color} />;
                    })()}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    Based on AI-powered aggregation of expert opinions and news stories
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button component={RouterLink} to="/sentiment" size="small" color="primary">
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            
            {/* USD Sentiment Card */}
            <Grid item xs={12} sm={6} md={5}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom align="center">
                    USD Sentiment
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption">Bullish</Typography>
                    <Typography variant="caption">Bearish</Typography>
                  </Box>
                  
                  <LinearProgress
                    variant="determinate"
                    value={sentimentData.sentiment_usd.percent_bullish}
                    color="primary"
                    sx={{ height: 10, borderRadius: 5, mb: 1 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">{sentimentData.sentiment_usd.percent_bullish.toFixed(1)}%</Typography>
                    <Typography variant="body2">{sentimentData.sentiment_usd.percent_bearish.toFixed(1)}%</Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'center', my: 2 }}>
                    {(() => {
                      const sentiment = getMarketSentiment(
                        sentimentData.sentiment_usd.percent_bullish,
                        sentimentData.sentiment_usd.percent_bearish
                      );
                      return <Chip label={sentiment.label} color={sentiment.color} />;
                    })()}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                    Based on AI-powered aggregation of expert opinions and news stories
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button component={RouterLink} to="/sentiment" size="small" color="primary">
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        ) : null}
      </Box>

      {/* Trading Bots Section */}
      <TradingBotsSection />
      <Box sx={{ textAlign: 'center', mt: 2, mb: 6 }}>
        <Button 
          component={RouterLink} 
          to="/bot-performance" 
          variant="outlined" 
          color="secondary"
          size="large"
        >
          View Bot Performance Stats
        </Button>
      </Box>

      {/* Analyst Directory Preview */}
      <Box component="section" sx={{ mb: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom color="secondary.main">
            Market Analysts Directory
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Discover respected voices in Gold and USD markets
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          XAUJournal maintains a comprehensive directory of prominent analysts who regularly share insights on Gold, USD, and related markets. These analysts are not affiliated with XAUJournal.
        </Alert>

        <Grid container spacing={3}>
          {featuredExperts.map(expert => (
            <Grid item key={expert.id} xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {expert.imageUrl && (
                  <CardMedia
                    component="img"
                    height="160" // Adjust height as needed
                    image={expert.imageUrl}
                    alt={expert.name}
                    sx={{ objectFit: 'cover' }} // Ensure image covers the area
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="h3">
                    {expert.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {expert.role || expert.title}
                  </Typography>
                  <Typography variant="body2">
                    {expert.shortBio || expert.bio?.substring(0, 120) + '...'}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-start', pl: 2, pb: 2 }}>
                  <Button component={RouterLink} to={`/experts/${expert.id}`} size="small" color="primary">
                    View Profile
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            component={RouterLink}
            to="/experts"
            variant="contained"
            size="large"
            color="primary"
            sx={{ color: 'secondary.main' }}
          >
            Browse Full Directory
          </Button>
        </Box>
      </Box>

      {/* Call to Action */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'primary.light',
          py: 6,
          px: 2,
          textAlign: 'center',
          borderRadius: theme.shape.borderRadius,
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" gutterBottom color="secondary.main">
            Stay Informed on Market Trends
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Subscribe to our newsletter for regular updates on Gold and USD market sentiment
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="primary"
            sx={{ color: 'secondary.main' }}
          >
            Subscribe to Newsletter
          </Button>
        </Container>
      </Paper>
    </Box>
  );
};

export default HomePage;